import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Sparkles, Star, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/lib/AuthProvider';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { 
  calculateRewardProgress
} from '@/services/rewardsService';
import { FreeItemReward, RewardProgress } from '@/types/rewards';
import { fetchProductByIdForRewards } from '@/services/productService';
import confetti from 'canvas-confetti';

interface RewardProgressBarProps {
  cartTotal: number;
  convertedCartTotal?: number;
}

export function RewardProgressBar({ cartTotal, convertedCartTotal }: RewardProgressBarProps) {
  const [rewardProgress, setRewardProgress] = useState<RewardProgress | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [processingRewards, setProcessingRewards] = useState(false);
  const [previousCartTotal, setPreviousCartTotal] = useState<number>(0);
  const [previousConvertedCartTotal, setPreviousConvertedCartTotal] = useState<number>(0);
  const [previousEarnedRewards, setPreviousEarnedRewards] = useState<string[]>([]);
  const [previousEarnedMilestones, setPreviousEarnedMilestones] = useState<string[]>([]);
  
  const { user } = useAuthContext();
  const { addToCart, removeFromCart, cartItems } = useCart();
  const { toast } = useToast();
  const { selectedCurrency, getConvertedItemPrice, formatDisplayAmount } = useCurrency();

  // Memoize calculated values early to prevent hook violations (must be before any conditional returns)
  const totalEarned = useMemo(() => {
    if (!rewardProgress) return 0;
    const { earnedRewards, earnedMilestones } = rewardProgress;
    return earnedRewards.length + (earnedMilestones?.length || 0);
  }, [rewardProgress]);
  
  const nextThresholdAmount = useMemo(() => {
    if (!rewardProgress?.nextThreshold) return null;
    // Calculate remaining amount in display currency for mathematical precision
    // This ensures: convertedCurrent + convertedRemaining = convertedTarget
    const convertedTarget = getConvertedItemPrice(rewardProgress.nextThreshold);
    
    // Use precise converted cart total if available, otherwise fall back to individual conversion
    const convertedCurrent = convertedCartTotal !== undefined 
      ? convertedCartTotal 
      : getConvertedItemPrice(cartTotal);
    
    const convertedRemaining = Math.max(0, convertedTarget - convertedCurrent);
    return formatDisplayAmount(convertedRemaining);
  }, [rewardProgress?.nextThreshold, cartTotal, convertedCartTotal, getConvertedItemPrice, formatDisplayAmount]);

  // Get the next milestone name for the "from winning" text (moved up to follow hooks rules)
  const nextMilestoneName = useMemo(() => {
    // Prioritize product rewards over milestone-only rewards for display
    if (rewardProgress?.nextReward && rewardProgress.nextReward.productName) {
      return rewardProgress.nextReward.productName;
    }
    // Fall back to milestone label
    if (rewardProgress?.nextMilestoneLabel) {
      return rewardProgress.nextMilestoneLabel;
    }
    // Generic fallback
    return "your next reward";
  }, [rewardProgress?.nextReward, rewardProgress?.nextMilestoneLabel]);

  // Define handler functions first to avoid circular dependencies
  const handleEarnedRewards = useCallback(async (earnedRewards: FreeItemReward[]) => {
    if (processingRewards) return;
    
    setProcessingRewards(true);
    
    try {
      // Add free products to cart for all newly earned rewards
      for (const reward of earnedRewards) {
        try {
          if (!reward.productId) {
            // Skip milestone-only rewards silently (this is expected behavior)
            continue;
          }
          
          // Check if this free item is already in the cart
          const existingCartItem = cartItems.find(
            item => item.id === reward.productId && item.price === 0
          );
          
          if (existingCartItem) {
            // Free item already in cart, skip adding it again
            console.log(`Free item ${reward.productName} already in cart, skipping add.`);
            continue;
          }
          
          const product = await fetchProductByIdForRewards(reward.productId);
          
          if (!product) {
            console.error(`Product with ID ${reward.productId} no longer exists for reward "${reward.productName || 'Unknown'}". Skipping reward.`);
            continue;
          }
          
          // Validate product has required fields for cart
          if (!product.id || !product.name || product.price === undefined || !product.image) {
            console.error('Product missing required fields:', product);
            continue;
          }
          
          // Ensure images array exists for CartContext compatibility
          if (!product.images || !Array.isArray(product.images)) {
            product.images = product.image ? [product.image] : [];
          }
          
          // Only add if product price is 0 (free item)
          if (product.price === 0) {
            addToCart(product, 1);
            
            // Add toast notification for successful cart addition (separate from celebration toast)
            toast({
              title: "Free Item Added!",
              description: `${product.name} has been added to your cart.`,
              variant: "default",
            });
          }
        } catch (error) {
          console.error('Error adding reward product to cart:', error);
          toast({
            title: "Error Adding Free Item",
            description: `Failed to add ${reward.productName || 'free item'} to cart.`,
            variant: "destructive",
          });
        }
      }

      // Trigger confetti and celebration
      triggerCelebration(earnedRewards);
      
    } catch (error) {
      console.error('Error handling earned rewards:', error);
    } finally {
      setProcessingRewards(false);
    }
  }, [processingRewards, addToCart, toast, cartItems]);

  const handleEarnedMilestones = useCallback(async (earnedMilestones: FreeItemReward[]) => {
    if (processingRewards) return;
    
    try {
      // Trigger confetti and milestone celebration for all newly earned milestones
      triggerMilestoneCelebration(earnedMilestones);
      
    } catch (error) {
      console.error('Error handling earned milestones:', error);
    }
  }, [processingRewards]);

  const handleLostRewards = useCallback(async (lostRewardIds: string[]) => {
    if (processingRewards) return;
    
    try {
      // Remove free products from cart that are no longer earned
      for (const rewardId of lostRewardIds) {
        // Find the reward to get the product ID
        const rewardConfig = rewardProgress?.earnedRewards.find(r => r.id === rewardId) || 
                            rewardProgress?.earnedMilestones.find(r => r.id === rewardId);
        
        if (rewardConfig?.productId) {
          // Find the product in cart items
          const cartItem = cartItems.find(item => item.id === rewardConfig.productId && item.price === 0);
          
          if (cartItem) {
            removeFromCart(cartItem.id);
            
            toast({
              title: "Free Item Removed",
              description: `${cartItem.name} was removed as the milestone is no longer met.`,
              variant: "default",
            });
          }
        }
      }
    } catch (error) {
      console.error('Error handling lost rewards:', error);
    }
  }, [processingRewards, removeFromCart, cartItems, rewardProgress, toast]);

  const triggerCelebration = (rewards: FreeItemReward[]) => {
    // Trigger confetti animation
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#00742F', '#FFC901', '#FFE55C', '#4ADE80', '#22C55E']
    });

    // Show toast notification
    rewards.forEach((reward, index) => {
      setTimeout(() => {
        toast({
          title: "🎉 Free Item Earned!",
          description: `${reward.productName} has been added to your cart!`,
          duration: 5000,
        });
      }, index * 1000);
    });
  };

  const triggerMilestoneCelebration = (milestones: FreeItemReward[]) => {
    // Trigger confetti animation
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#00742F', '#FFC901', '#FFE55C', '#4ADE80', '#22C55E']
    });

    // Show toast notification for milestones
    milestones.forEach((milestone, index) => {
      setTimeout(() => {
        toast({
          title: "🎯 Milestone Achieved!",
          description: milestone.milestoneLabel || "You've reached a new milestone!",
          duration: 5000,
        });
      }, index * 1000);
    });
  };

  // Define the update progress function after the handlers
  const updateProgress = useCallback(async () => {
    // Only show loading on initial mount, not on cart changes
    if (initialLoading) {
      setInitialLoading(true);
    }
    
    try {
      // Pass display currency options for mathematical precision
      const progress = await calculateRewardProgress(cartTotal, {
        convertedCartTotal,
        currencyCode: selectedCurrency,
        getConvertedItemPrice,
      });
      setRewardProgress(progress);
      
      // Detect threshold crossings with display currency precision
      const shouldUseDisplayCurrency = convertedCartTotal !== undefined && selectedCurrency !== 'USD';
      
      // Function to check if threshold was just crossed
      const wasThresholdJustCrossed = (threshold: number): boolean => {
        if (shouldUseDisplayCurrency) {
          // Use display currency precision for celebration detection
          const convertedThreshold = getConvertedItemPrice(threshold);
          const currentConverted = convertedCartTotal!;
          const previousConverted = previousConvertedCartTotal;
          
          // Check if we crossed the threshold: was below, now above
          return previousConverted < convertedThreshold && currentConverted >= convertedThreshold;
        } else {
          // Use USD logic
          return previousCartTotal < threshold && cartTotal >= threshold;
        }
      };
      
      // Function to check if threshold was just lost
      const wasThresholdJustLost = (threshold: number): boolean => {
        if (shouldUseDisplayCurrency) {
          // Use display currency precision
          const convertedThreshold = getConvertedItemPrice(threshold);
          const currentConverted = convertedCartTotal!;
          const previousConverted = previousConvertedCartTotal;
          
          // Check if we fell below the threshold: was above, now below
          return previousConverted >= convertedThreshold && currentConverted < convertedThreshold;
        } else {
          // Use USD logic
          return previousCartTotal >= threshold && cartTotal < threshold;
        }
      };
      
      // Find newly earned rewards using display currency precision
      const newlyEarnedRewards = progress.earnedRewards.filter(reward => 
        wasThresholdJustCrossed(reward.threshold)
      );
      
      // Find newly earned milestones using display currency precision
      const newlyEarnedMilestones = progress.earnedMilestones.filter(milestone => 
        wasThresholdJustCrossed(milestone.threshold)
      );
      
      // Find lost rewards (for cart decrease)
      const lostRewards = previousEarnedRewards.filter(prevRewardId => 
        !progress.earnedRewards.some(reward => reward.id === prevRewardId)
      );
      
      // Trigger celebrations for newly crossed thresholds
      if (newlyEarnedRewards.length > 0) {
        await handleEarnedRewards(newlyEarnedRewards);
      }
      
      if (newlyEarnedMilestones.length > 0) {
        await handleEarnedMilestones(newlyEarnedMilestones);
      }
      
      // Handle lost rewards
      if (lostRewards.length > 0) {
        await handleLostRewards(lostRewards);
      }
      
      // Update tracking
      setPreviousCartTotal(cartTotal);
      setPreviousConvertedCartTotal(convertedCartTotal || 0);
      setPreviousEarnedRewards(progress.earnedRewards.map(r => r.id));
      setPreviousEarnedMilestones(progress.earnedMilestones.map(m => m.id));
    } catch (error) {
      console.error('Error updating reward progress:', error);
    } finally {
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  }, [cartTotal, convertedCartTotal, selectedCurrency, getConvertedItemPrice, initialLoading, previousCartTotal, previousConvertedCartTotal, previousEarnedRewards, previousEarnedMilestones, handleEarnedRewards, handleEarnedMilestones, handleLostRewards]);

  // Override progress calculation with display currency precision when convertedCartTotal is available
  const displayCurrencyProgress = useMemo(() => {
    if (!rewardProgress || convertedCartTotal === undefined || selectedCurrency === 'USD') {
      return null; // Use original USD-based calculation
    }

    // Do milestone detection in display currency space for precision
    if (!rewardProgress.nextThreshold) {
      return 100; // No next threshold means all milestones reached
    }

    const convertedTarget = getConvertedItemPrice(rewardProgress.nextThreshold);
    
    if (convertedCartTotal >= convertedTarget) {
      // We've reached or exceeded the target threshold in display currency
      return 100;
    }

    // Calculate progress in display currency space
    const allRewards = [...(rewardProgress.earnedRewards || []), ...(rewardProgress.earnedMilestones || [])];
    const previousThreshold = allRewards.length > 0 
      ? Math.max(...allRewards.map(r => getConvertedItemPrice(r.threshold)))
      : 0;
    
    const progressRange = convertedTarget - previousThreshold;
    const currentProgress = convertedCartTotal - previousThreshold;
    
    return progressRange > 0 
      ? Math.min(100, Math.max(0, (currentProgress / progressRange) * 100))
      : 0;
  }, [rewardProgress, convertedCartTotal, selectedCurrency, getConvertedItemPrice]);

  // Detect milestone achievements in display currency space
  const displayCurrencyMilestoneStatus = useMemo(() => {
    if (!rewardProgress || convertedCartTotal === undefined || selectedCurrency === 'USD') {
      return { milestoneReached: false, shouldCelebrate: false };
    }

    if (!rewardProgress.nextThreshold) {
      return { milestoneReached: true, shouldCelebrate: false }; // All milestones already reached
    }

    const convertedTarget = getConvertedItemPrice(rewardProgress.nextThreshold);
    const milestoneReached = convertedCartTotal >= convertedTarget;

    // Check if this is a newly reached milestone (to trigger celebration)
    const convertedPrevious = convertedCartTotal !== undefined ? convertedCartTotal : 0;
    const shouldCelebrate = milestoneReached && !rewardProgress.earnedMilestones?.some(m => 
      getConvertedItemPrice(m.threshold) <= convertedTarget
    );

    return { milestoneReached, shouldCelebrate };
  }, [rewardProgress, convertedCartTotal, selectedCurrency, getConvertedItemPrice]);

  // Calculate progress when cart total changes
  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  if (initialLoading || !rewardProgress) {
    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-jamaica-green/5 to-jamaica-yellow/5 rounded-xl border border-jamaica-green/20">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="h-4 w-4 text-jamaica-green animate-pulse" />
          <span className="text-sm font-medium text-gray-700">Loading rewards...</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    );
  }

  // Don't show if no rewards are configured
  if (!rewardProgress.hasActiveRewards) {
    return null;
  }

  const { progress, nextReward, nextThreshold, earnedRewards, earnedMilestones } = rewardProgress;
  
  // Use display currency progress when available for precision, otherwise use USD-based progress
  const finalProgress = displayCurrencyProgress !== null ? displayCurrencyProgress : progress;

  return (
    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-jamaica-green/5 to-jamaica-yellow/5 rounded-xl border border-jamaica-green/20 relative overflow-hidden">
      {/* Background sparkle effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-2 right-4 w-1 h-1 bg-jamaica-yellow rounded-full animate-ping delay-300"></div>
        <div className="absolute bottom-3 left-6 w-1 h-1 bg-jamaica-green rounded-full animate-ping delay-700"></div>
        <div className="absolute top-4 left-1/3 w-0.5 h-0.5 bg-jamaica-yellow rounded-full animate-pulse delay-500"></div>
      </div>

      {/* Header */}
      <div className="mb-3 relative">
        <div className="flex items-center gap-2 flex-wrap">
          <motion.div
            animate={{ rotate: totalEarned > 0 ? [0, 15, -15, 0] : 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <Gift className="h-4 w-4 text-jamaica-green" />
          </motion.div>
          <span className="text-sm font-medium text-gray-700">Free Item Rewards</span>
          {totalEarned > 0 && (
            <Badge className="bg-jamaica-green text-white text-[10px] px-1.5 py-0.5 animate-pulse">
              {totalEarned} Earned!
            </Badge>
          )}
        </div>
        
        {nextThresholdAmount && (
          <div className="mt-1.5 text-[8px] sm:text-[9px] text-gray-500">
            <span className="font-medium text-jamaica-green">
              You're {nextThresholdAmount} away from winning {nextMilestoneName}
            </span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="relative mb-2 sm:mb-3">
        <div className="h-2.5 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-jamaica-green to-jamaica-yellow relative overflow-hidden"
            animate={{ width: `${finalProgress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
          </motion.div>
        </div>
        
        {/* Progress markers for thresholds */}
        {nextReward && (
          <div className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-3 h-3 sm:w-4 sm:h-4 bg-jamaica-yellow rounded-full border-2 border-white shadow-lg flex items-center justify-center"
            >
              <Star className="h-1.5 w-1.5 sm:h-2 sm:w-2 text-white" />
            </motion.div>
          </div>
        )}
      </div>

      {/* Current status */}
      <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-600">
        <span className="font-medium">
          You've spent {convertedCartTotal !== undefined 
            ? formatDisplayAmount(convertedCartTotal)
            : formatDisplayAmount(getConvertedItemPrice(cartTotal))
          }
        </span>
        {nextThreshold && (
          <span className="font-medium">
            {formatDisplayAmount(getConvertedItemPrice(nextThreshold))}
          </span>
        )}
      </div>

      {/* Earned rewards display */}
      <AnimatePresence>
        {totalEarned > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-jamaica-green/20"
          >
            <div className="flex items-center gap-2 text-jamaica-green">
              <Sparkles className="h-3 w-3 flex-shrink-0" />
              <span className="text-[10px] sm:text-xs font-medium">
                {earnedRewards.length > 0 && earnedMilestones && earnedMilestones.length > 0
                  ? "Free items & milestones earned!"
                  : earnedRewards.length > 0
                  ? "Free items added to your cart!"
                  : "Milestones reached!"
                }
              </span>
            </div>
            <div className="mt-1.5 sm:mt-2 space-y-1">
              {/* Display earned free products */}
              {earnedRewards.map((reward) => (
                <div key={reward.id} className="flex items-center gap-2 text-[10px] sm:text-xs">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-jamaica-green rounded-full flex-shrink-0"></div>
                  <span className="text-gray-700 truncate flex-1">{reward.productName}</span>
                  <Badge className="bg-jamaica-gold text-white px-1 py-0 text-[8px] sm:text-xs flex-shrink-0">
                    FREE
                  </Badge>
                </div>
              ))}
              
              {/* Display earned milestones */}
              {earnedMilestones && earnedMilestones.map((milestone) => (
                <div key={milestone.id} className="flex items-center gap-2 text-[10px] sm:text-xs">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gradient-to-r from-jamaica-green to-jamaica-yellow rounded-full flex-shrink-0"></div>
                  <span className="text-gray-700 truncate flex-1">
                    {milestone.milestoneLabel || "Milestone Reached"}
                  </span>
                  <Badge className="bg-jamaica-green text-white px-1 py-0 text-[8px] sm:text-xs flex-shrink-0">
                    ACHIEVED
                  </Badge>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Next reward preview */}
      {nextReward && nextReward.productName && nextReward.productImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src={nextReward.productImage}
              alt={nextReward.productName}
              className="w-6 h-6 sm:w-8 sm:h-8 rounded-md object-cover border border-gray-200 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-medium text-gray-700 truncate">
                Next: {nextReward.productName}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                At {formatDisplayAmount(getConvertedItemPrice(nextReward.threshold))}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Next milestone preview (for milestone-only rewards) */}
      {nextThreshold && (!nextReward || !nextReward.productName) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-md bg-gradient-to-r from-jamaica-green to-jamaica-yellow flex items-center justify-center flex-shrink-0">
              <Target className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-medium text-gray-700 truncate">
                Next: {rewardProgress.nextMilestoneLabel || "Milestone"}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                At {formatDisplayAmount(getConvertedItemPrice(nextThreshold))}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}